import { useCofheConnection } from '@/hooks';
import { usePortalNavigation, usePortalStatuses } from '@/stores';
import { usePortalUI } from '@/stores/portalUIStore';
import { cn } from '@/utils';
import { useMemo } from 'react';
import { IoIosCheckmarkCircleOutline, IoIosCloseCircleOutline } from 'react-icons/io';
import { MdOutlineSettings } from 'react-icons/md';
import { FhenixLogoIcon } from '../FhenixLogoIcon';
import { AnimatedZStack } from '../primitives/AnimatedZStack';
import { useCofheFloatingButtonContext } from './CofheFloatingButtonContext';
import type { CofheFloatingButtonInternalStatus } from './internalTypes';
import { FloatingButtonPage } from './pagesConfig/types';
import { COFHE_STATUS_IDS, type CofheStatusActionIntent, type CofheStatusVariant } from './types';

const ConnectionStatus: React.FC = () => {
  const { theme } = useCofheFloatingButtonContext();
  const { navigateTo } = usePortalNavigation();
  const { connected } = useCofheConnection();

  return (
    <div className="cofhe-panel w-full h-full flex px-4 items-center justify-between">
      {/* Logo Icon */}
      <FhenixLogoIcon theme={theme} className="w-10 h-10" />

      {/* Status */}
      <div className="flex items-center gap-1 ml-auto mr-2">
        {connected ? (
          <>
            <IoIosCheckmarkCircleOutline className="text-green-500" />
            <span className="font-medium" aria-live="polite">
              Connected
            </span>
          </>
        ) : (
          <>
            <IoIosCloseCircleOutline className="text-red-500" />
            <span className="font-medium" aria-live="polite">
              Disconnected
            </span>
          </>
        )}
      </div>

      {/* Settings Icon */}
      <button
        onClick={() => navigateTo(FloatingButtonPage.Settings)}
        className={cn('p-1 rounded cofhe-hover-overlay transition-colors', 'cofhe-text-primary')}
      >
        <MdOutlineSettings className="w-4 h-4" />
      </button>
    </div>
  );
};

const statusTextColorMap: Record<CofheStatusVariant, string> = {
  error: 'text-red-500',
  warning: 'text-yellow-500',
  info: 'text-blue-500',
};

const statusBorderColorMap: Record<CofheStatusVariant, string> = {
  error: 'border-red-500',
  warning: 'border-yellow-500',
  info: 'border-blue-500',
};

function resolveStatusIntentOnClick(
  intent: CofheStatusActionIntent,
  actions: {
    navigateTo: (page: FloatingButtonPage) => void;
    replace: (page: FloatingButtonPage) => void;
    openPortal: () => void;
  }
): () => void {
  switch (intent) {
    case 'open-acps':
      return () => {
        actions.openPortal();
        actions.replace(FloatingButtonPage.ACPs);
      };
    case 'open-claimable-tokens':
      return () => {
        actions.openPortal();
        actions.navigateTo(FloatingButtonPage.ClaimableTokens);
      };
  }
}

const ActiveStatusContent: React.FC<{ status: CofheFloatingButtonInternalStatus }> = ({ status }) => {
  const { theme } = useCofheFloatingButtonContext();
  const { navigateTo, replace } = usePortalNavigation();
  const { openPortal } = usePortalUI();

  const actionOnClick =
    status.action == null
      ? undefined
      : 'intent' in status.action
        ? resolveStatusIntentOnClick(status.action.intent, { navigateTo, replace, openPortal })
        : status.action.onClick;

  return (
    <div
      className={cn(
        'cofhe-panel w-full h-full flex px-4 items-center justify-between',
        statusBorderColorMap[status.variant]
      )}
    >
      {/* Logo Icon */}
      <FhenixLogoIcon theme={theme} className="w-10 h-10" />

      {/* Status Info */}
      <div className="flex flex-col ml-2 mr-auto">
        <h3 className={cn('text-sm font-medium', statusTextColorMap[status.variant])}>{status.title}</h3>
        {status.description != null && (
          <p className={cn('text-xs', statusTextColorMap[status.variant])}>{status.description}</p>
        )}
      </div>

      {/* Action Button */}
      {status.action != null && actionOnClick != null && (
        <button
          onClick={actionOnClick}
          className={cn('p-1 rounded cofhe-hover-overlay transition-colors', 'cofhe-text-primary')}
        >
          {status.action.label}
        </button>
      )}
    </div>
  );
};

const STATUSES_ORDER = new Map<string, number>(
  [
    // first always goes "claims available" as claiming doesn't require an ACP
    COFHE_STATUS_IDS.claimsAvailable,

    // next goes all acp related statuses
    COFHE_STATUS_IDS.missingACP,
    COFHE_STATUS_IDS.acpExpired,
    COFHE_STATUS_IDS.acpExpiringSoon,
    COFHE_STATUS_IDS.acpShared,

    // next everything else can be sorted by time or just left in the order they were added
  ]
    .reverse()
    .map((id, index) => [id, index])
);

export function sortCofheStatuses(
  a: Pick<CofheFloatingButtonInternalStatus, 'id'>,
  b: Pick<CofheFloatingButtonInternalStatus, 'id'>
): number {
  const aIndex = STATUSES_ORDER.get(a.id) ?? -1;
  const bIndex = STATUSES_ORDER.get(b.id) ?? -1;

  if (aIndex === -1 && bIndex === -1) {
    // If both statuses are not in the predefined order, keep their original order
    return 0;
  }
  if (aIndex === -1) {
    // If only a is not in the predefined order, b should come first
    return 1;
  }
  if (bIndex === -1) {
    // If only b is not in the predefined order, a should come first
    return -1;
  }
  // If both statuses are in the predefined order, sort by their index
  return aIndex - bIndex;
}

export const StatusBarContent: React.FC = () => {
  const { statuses } = usePortalStatuses();

  const sortedStatuses = useMemo(() => statuses.slice().sort(sortCofheStatuses), [statuses]);

  return (
    <AnimatedZStack>
      {/* Connection status showing connection state and chain */}
      <ConnectionStatus />

      {/* Active errors or warnings to be resolved */}
      {sortedStatuses.map((status) => (
        <ActiveStatusContent key={status.id} status={status} />
      ))}
    </AnimatedZStack>
  );
};
